'use client';

import { useMemo } from 'react';
import { ScanText } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useEchoConnection } from '@/hooks/use-echo-connection';
import { cn } from '@/lib/utils';

import { useActiveOcrJobs } from '../hooks/use-active-ocr-jobs';
import { useOcrUserChannel } from '../hooks/use-ocr-user-channel';
import { useOcrToast } from '../utils/ocr-toast';
import { ActiveOcrJobsDropdown } from './active-ocr-jobs-dropdown';

export function ActiveOcrJobsBadge() {
  const t = useTranslations('ocr');
  const { data, isLoading } = useActiveOcrJobs();
  const { shouldFallback } = useEchoConnection();
  const toasts = useOcrToast();

  useOcrUserChannel({
    onCompleted: (event) => toasts.completed(event),
    onFailed: (event) => toasts.failed(event)
  });

  const jobs = data?.data ?? [];
  const count = jobs.length;
  const hasActive = count > 0;

  const tooltip = useMemo(() => {
    if (!hasActive) return t('activeJobs.empty');
    return t('activeJobs.activeCount', { count });
  }, [hasActive, count, t]);

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='relative h-9 w-9'
              aria-label={tooltip}
              disabled={isLoading && !hasActive}
            >
              <ScanText
                className={cn('h-[18px] w-[18px]', hasActive && 'text-primary')}
              />
              {hasActive && (
                <span
                  className={cn(
                    'bg-primary text-primary-foreground absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                    'animate-in zoom-in-50'
                  )}
                  aria-hidden
                >
                  {count > 9 ? '9+' : count}
                </span>
              )}
              {hasActive && (
                <span
                  className={cn(
                    'absolute end-0.5 bottom-0.5 h-1.5 w-1.5 rounded-full',
                    shouldFallback ? 'bg-amber-500' : 'bg-emerald-500',
                    !shouldFallback && 'animate-pulse'
                  )}
                  aria-hidden
                />
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side='bottom'>{tooltip}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align='end' className='w-[340px] p-0' sideOffset={8}>
        <ActiveOcrJobsDropdown jobs={jobs} fallbackToPolling={shouldFallback} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
